'use client'

import type {
  ProductListingReviewsProviderProps,
  ProductListingReviewsSummaryContextProps,
} from '@maestro/commerce-blocks/providers/product-listing-reviews/types'
import type { ProductIdToReviewSummaryMap } from '@maestro/data-layer'

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'

import { createLogger } from '@maestro/core/logger'
import { theme } from '@maestro/local/theme'

const log = createLogger('maestro:commerce-blocks:reviews')

const ProductListingReviewsContext =
  createContext<ProductListingReviewsSummaryContextProps | null>(null)

export const ProductListingReviewsProvider = ({
  children,
  value,
}: {
  children: React.ReactNode
  value?: ProductListingReviewsProviderProps
}) => {
  const productIdsToReviewSummaryMapPromise =
    value?.productIdsToReviewSummaryMapPromise

  const showReviewSummary =
    theme().product.isReviewsEnabled && theme().product.showReviewSummary

  const [reviewSummaryPromise, setReviewSummaryPromise] = useState<
    Promise<ProductIdToReviewSummaryMap | undefined> | undefined
  >(showReviewSummary ? productIdsToReviewSummaryMapPromise : undefined)

  const [isLoading, setIsLoading] = useState(showReviewSummary)

  const [productIdsToRatingsMap, setProductIdsToRatingsMap] =
    useState<ProductIdToReviewSummaryMap>({})

  useEffect(() => {
    if (reviewSummaryPromise) {
      const fetchReviewSummary = async () => {
        setIsLoading(true)
        try {
          setProductIdsToRatingsMap((await reviewSummaryPromise) ?? {})
        } catch (_error) {
          log.warn('Error fetching review summaries')
        } finally {
          setIsLoading(false)
        }
      }
      fetchReviewSummary()
    }
  }, [reviewSummaryPromise, showReviewSummary])

  const getProductReviewSummary = useCallback(
    (productId: string) => {
      return productIdsToRatingsMap?.[productId]
    },
    [productIdsToRatingsMap]
  )

  return (
    <ProductListingReviewsContext.Provider
      value={{
        isLoadingReviewSummary: isLoading,
        getProductReviewSummary,
        showReviewSummary,
        setReviewSummaryPromise,
      }}
    >
      {children}
    </ProductListingReviewsContext.Provider>
  )
}

export const useProductListingReviewsContext = () => {
  const context = useContext(ProductListingReviewsContext)
  if (!context) {
    throw new Error(
      'useProductListingReviewsContext must be used within a ProductListingReviewsProvider'
    )
  }
  return context
}
